import { useState, useEffect, useRef } from "react";
import { ExternalLink, ShieldCheck, Save, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_OPTS = [
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "discrepancy", label: "Discrepancy Found" },
  { value: "not_applicable", label: "Not Applicable" },
];

const STATUS_CLS = {
  verified: "text-[#1D504C] font-medium",
  discrepancy: "text-red-700 font-medium",
  pending: "text-gray-500",
  not_applicable: "text-gray-400",
};

const VerificationItemRow = ({ v, entry, uploading, onField, onPickFile }) => (
  <div data-testid={`verification-item-${v.id}`} className="border-b border-[#113835]/8 pb-5 last:border-0 last:pb-0">
    <div className="flex items-center justify-between gap-4 mb-3">
      <p className="font-heading text-sm font-medium text-[#113835]">{v.label}</p>
      <a href={v.url} target="_blank" rel="noopener noreferrer"
        data-testid={`verification-portal-link-${v.id}`}
        className="text-[0.68rem] uppercase tracking-widest text-[#C1A74A] hover:text-[#113835] transition-colors flex items-center gap-1 shrink-0">
        {v.portal} <ExternalLink size={11} strokeWidth={1.5} />
      </a>
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      <select data-testid={`verification-status-${v.id}`} className={`tt-input !py-2 text-sm ${STATUS_CLS[entry.status || "pending"]}`}
        value={entry.status || "pending"} onChange={(e) => onField(v.id, "status", e.target.value)}>
        {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <input data-testid={`verification-reference-${v.id}`} className="tt-input !py-2 text-sm" placeholder="Reference no. / date"
        value={entry.reference || ""} onChange={(e) => onField(v.id, "reference", e.target.value)} />
      <input data-testid={`verification-remarks-${v.id}`} className="tt-input !py-2 text-sm" placeholder="Remarks"
        value={entry.remarks || ""} onChange={(e) => onField(v.id, "remarks", e.target.value)} />
    </div>
    <button type="button" data-testid={`verification-upload-${v.id}`} onClick={() => onPickFile(v.id)}
      disabled={uploading}
      className="mt-3 text-[0.7rem] uppercase tracking-widest text-[#1D504C] hover:text-[#C1A74A] transition-colors flex items-center gap-1.5 disabled:opacity-50">
      {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} strokeWidth={1.5} />}
      {entry.document_name || (entry.document_id ? "Copy attached" : "Attach signed copy")}
    </button>
  </div>
);

export const VerificationPanel = ({ item, onSaved }) => {
  const [schema, setSchema] = useState(null);
  const [entries, setEntries] = useState(item.verification?.items || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(item.verification || null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  useEffect(() => {
    api.get("/admin/verification-schema").then(({ data }) => setSchema(data)).catch(() => {});
  }, []);

  if (!schema) return null;

  const setField = (id, field, value) =>
    setEntries((prev) => ({ ...prev, [id]: { ...(prev[id] || { status: "pending" }), [field]: value } }));

  const pickFile = (id) => {
    uploadTarget.current = id;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const id = uploadTarget.current;
    if (!file || !id) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setField(id, "document_id", data.id);
      setField(id, "document_name", file.name);
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/inquiries/${item.id}/verification`, { items: entries });
      setSaved(data);
      toast.success(data.source_verified ? "Saved — SOURCE VERIFIED" : "Verification checklist saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="verification-panel" className="border border-[#113835]/20">
      <div className="bg-[#F9FAFA] border-b border-[#113835]/10 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="font-heading text-xs tracking-[0.2em] uppercase text-[#1D504C]">Govt Record Verification</p>
          <p className="text-[0.65rem] text-gray-400 mt-1">MahaBhulekh · IGR e-Search · Digital Satbara</p>
        </div>
        {saved?.source_verified && (
          <span data-testid="source-verified-badge" className="tt-status-badge bg-[#C1A74A]/20 text-[#8a6d1f] flex items-center gap-1.5">
            <ShieldCheck size={13} strokeWidth={1.5} /> Source Verified
          </span>
        )}
      </div>

      <div className="p-6 space-y-6">
        <input ref={fileRef} type="file" data-testid="verification-file-input" className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onFile} />
        {schema.items.map((v) => (
          <VerificationItemRow key={v.id} v={v} entry={entries[v.id] || {}}
            uploading={uploadingId === v.id} onField={setField} onPickFile={pickFile} />
        ))}

        <button data-testid="verification-save" onClick={save} disabled={saving}
          className="tt-btn-primary w-full justify-center !py-3 disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} strokeWidth={1.5} />}
          Save Verification Checklist
        </button>
      </div>
    </div>
  );
};
